import { createStore, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { hasProdLogsEnabled } from './utils/helpers';

export const initialState = {
  c1Accounts: {
    requestAccounts: false,
    hasError: false,
    c1AccountID: '',
    lastAccessedAccountID: '',
    c1AccountsList: [],
    c1RoleID: '',
    migratedServices: {},
    // TODO: remove this state when v1->v2 migration is done
    // isCloudOneBacked: true to avoid showing banner or causing redirect initially
    cloudOneBackedStatus: {
      isCloudOneBacked: true,
      showInitialMigrationPage: true,
    },
    c1CloudProviders: {},
  },
  c1Services: { fetchServices: false, hasError: false, c1ServicesList: [] },
  c1Token: { token: '', expires: '', hasError: false, fetchC1Token: false },
  c1UserDetails: { name: '', email: '', timezone: '', wsAdminID: '', hasMFAResetCode: null },
  c1Common: {
    storeId: 'web-app-mgmt-console',
    loggedIn: false,
    loggingOut: false,
    authDetails: { authType: '', expires: '' },
    appSelection: '',
    locale: 'en',
    theme: { colorMode: 'light', overrides: {} },
    isLoading: false,
  },
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'STORE_INIT':
      // rehydrate the states base on shell state (e.g. upon refresh)
      return {
        ...action.shellInitState,
        c1Common: {
          ...action.shellInitState.c1Common,
          storeId: 'web-app-mgmt-console',
        },
      };
    case 'NAVIGATE_APP':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          appSelection: action.appName,
        },
      };
    case 'STORE_C1_TOKEN':
      return {
        ...state,
        c1Token: {
          ...state.c1Token,
          ...action.c1Token,
        },
      };
    case 'ADD_LAST_SELECTED_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
        },
      };
    case 'ADD_ACCOUNT_ID':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: action.c1AccountID,
          c1RoleID: action.c1RoleID,
        },
      };
    case 'REMOVE_ACCOUNT':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountID: '',
          lastAccessedAccountID: '',
          c1AccountsList: state.c1Accounts.c1AccountsList.filter(
            (account) => account.id !== action.c1AccountID
          ),
        },
      };
    case 'UPDATE_ACCOUNT':
      const { accountId, accountAlias, mfaRequired, locale, timezone } = action.accountDetails;
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: state.c1Accounts.c1AccountsList.map((account) => {
            if (account.id === accountId) {
              account.alias = accountAlias;
              account.mfaRequired = mfaRequired;
              account.locale = locale;
              account.timezone = timezone;
            }
            return account;
          }),
        },
      };
    case 'STORE_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1AccountsList: action.storeAccounts,
          requestsAccounts: false,
        },
      };
    case 'FETCH_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          fetchServices: action.fetchServices,
        },
      };
    case 'STORE_SERVICES':
      return {
        ...state,
        c1Services: {
          ...state.c1Services,
          c1ServicesList: action.c1ServicesList,
          hasError: false,
          errorMsg: '',
          fetchServices: false,
        },
      };
    case 'LOGGED_IN':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: true,
          loggingOut: false,
        },
      };
    case 'LOGGING_OUT':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          loggedIn: false,
          loggingOut: true,
        },
      };
    case 'ADD_AUTH_DETAILS':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          authDetails: {
            ...state.authDetails,
            ...action.authDetails,
          },
        },
      };
    case 'SET_LOCALE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          locale: action.locale,
        },
      };
    case 'SET_THEME':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: action.theme,
        },
      };
    case 'SET_COLOR_MODE':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            colorMode: action.colorMode,
          },
        },
      };
    case 'SET_THEME_OVERRIDES':
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          theme: {
            ...state.c1Common.theme,
            overrides: action.overrides,
          },
        },
      };
    case 'ADD_USER_INFO': {
      const { name = '', email = '', timezone = '' } = action.userDetails;
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          email,
          name,
          timezone,
        },
      };
    }
    case 'SHOW_LOADING_INDICATOR':
    case 'HIDE_LOADING_INDICATOR': {
      return {
        ...state,
        c1Common: {
          ...state.c1Common,
          isLoading: action.loading,
        },
      };
    }
    case 'ADD_CLOUDONE_BACKED_STATUS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...state.c1Accounts.cloudOneBackedStatus,
            isCloudOneBacked: action.isCloudOneBacked,
          },
        },
      };
    case 'RESET_CLOUDONE_BACKED_STATUS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...initialState.c1Accounts.cloudOneBackedStatus,
          },
        },
      };
    case 'TOGGLE_SHOW_INITIAL_MIGRATION_PAGE':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          cloudOneBackedStatus: {
            ...state.c1Accounts.cloudOneBackedStatus,
            showInitialMigrationPage: action.showInitialMigrationPage,
          },
        },
      };
    case 'UPDATE_CLOUD_PROVIDER_ACCOUNTS':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          c1CloudProviders: {
            ...state.c1Accounts.c1CloudProviders,
            [action.provider]: action.accounts,
          },
        },
      };
    case 'HYDRATE_STORE':
      return {
        ...state,
        ...action.storeState,
      };
    case 'RESET_STORE':
      return {
        ...state,
        ...initialState,
      };
    case 'TOGGLE_HAS_MFA_RESET_CODE':
      return {
        ...state,
        c1UserDetails: {
          ...state.c1UserDetails,
          hasMFAResetCode: action.hasMFAResetCode,
        },
      };
    case 'STORE_ACCOUNT_MIGRATED_SERVICES_STATE':
      return {
        ...state,
        c1Accounts: {
          ...state.c1Accounts,
          migratedServices: action.migratedServices,
        },
      };
    default:
      return state;
  }
}

export const getAccountDetails = (state, type) => state?.c1Accounts?.[type] || undefined;
export const getC1Token = (state, type) => state?.c1Token?.[type] || undefined;
export const getC1UserDetails = (state, type) =>
  type === 'userDetails'
    ? state?.c1UserDetails
    : (type !== '' && state?.c1UserDetails?.[type]) || '';
export const getC1Services = (state, type) => state?.c1Services?.[type] || undefined;
export const getAccountServicesMigratedState = (state, service = 'workload') =>
  state?.c1Accounts?.migratedServices?.[service]?.state;
export const getAccountInfo = (state, key = '') => {
  const currentAccountID = getAccountDetails(state, 'c1AccountID');
  const accountInfo = state?.c1Accounts?.c1AccountsList.find(
    (account) => account.id === currentAccountID
  );
  return accountInfo ? (key === '' ? accountInfo : accountInfo[key]) : '';
};

export const getCommonType = (state = {}, key = '') => {
  const c1CommonState = state?.c1Common;
  if (c1CommonState[key] !== undefined) {
    return c1CommonState[key];
  } else {
    for (const objProp in c1CommonState) {
      if (c1CommonState[objProp] && c1CommonState[objProp][key]) {
        return c1CommonState[objProp][key];
      }
    }
  }
};

/**
 * Custom Logger Middleware
 * @param store
 * @returns {function(*): function(*=): *}
 */
/**
 * Logs all actions and states after they are dispatched.
 */
const logger = (store) => (next) => (action) => {
  if (
    (DEPLOYMENT_TARGET !== 'prod' && DEPLOYMENT_TARGET !== 'govcloud-prod') ||
    hasProdLogsEnabled
  ) {
    console.group(action.type, 'web-app-mgmt-console');
    console.debug('web-app-mgmt-console dispatching', action);

    // Call the next dispatch method in the middleware chain
    const result = next(action);

    console.debug('web-app-mgmt-console next state after dispatch', store.getState());
    console.groupEnd();
    return result;
  }
  return next(action);
};

const composeEnhancers = composeWithDevTools({
  name: 'web-app-mgmt-console',
  trace: true,
  traceLimit: 25,
});
export const storeInstance = createStore(reducer, composeEnhancers(applyMiddleware(logger)));
